import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Steps,
  Table,
  Select,
  InputNumber,
  Input,
  Button,
  Space,
  Tag,
  Typography,
  App,
  Alert,
  Radio,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { bidAnalysisService, type BidAnalysis, type BidAnalysisSelection } from '@/services/bidAnalysis.service';
import {
  purchaseOrderService,
  type CreateFromBidAnalysesPayload,
  type EligiblePoForBaRevision,
} from '@/services/purchaseOrder.service';
import { projectLabel } from '@/utils/projectDisplay';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';

const { Text } = Typography;

type LineRow = {
  key: string;
  selectionId: string;
  bidAnalysisId: string;
  analysisNumber: string;
  itemDescription: string;
  itemCode?: string;
  projectLabel: string;
  hasProject: boolean;
  supplierId: string;
  supplierName: string;
  quotedQty: number;
  convertedQty: number;
  remainingQty: number;
  unitPrice: number;
  awardQty: number;
  included: boolean;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreatePOFromBidAnalysesModal: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [selectedBaIds, setSelectedBaIds] = useState<string[]>([]);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [createMode, setCreateMode] = useState<'new' | 'revise_existing'>('new');
  const [targetPoId, setTargetPoId] = useState<string | undefined>(undefined);
  const [lineRows, setLineRows] = useState<LineRow[]>([]);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [deliveryTerms, setDeliveryTerms] = useState('');
  const [quotationReference, setQuotationReference] = useState('');
  const [departmentId, setDepartmentId] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [selectionsLoading, setSelectionsLoading] = useState(false);

  const { data: approvedAnalyses = [], isLoading: analysesLoading } = useQuery({
    queryKey: ['bid-analyses', 'po-wizard-convertible'],
    queryFn: () => bidAnalysisService.listForPoConsolidation(),
    enabled: open,
  });

  const baById = useMemo(() => {
    const map = new Map<string, BidAnalysis>();
    for (const ba of approvedAnalyses) map.set(ba.id, ba);
    return map;
  }, [approvedAnalyses]);

  const currency = useMemo(() => {
    for (const id of selectedBaIds) {
      const ba = baById.get(id);
      const c = (ba as any)?.supplierInquiry?.currency || (ba as any)?.currency;
      if (c) return String(c);
    }
    return 'AED';
  }, [selectedBaIds, baById]);

  const { data: eligiblePos = [], isLoading: eligibleLoading } = useQuery({
    queryKey: ['po-eligible-ba-revision', supplierId, currency],
    queryFn: () => purchaseOrderService.listEligibleForBaRevision(supplierId!, currency),
    enabled: open && step === 2 && createMode === 'revise_existing' && !!supplierId,
  });

  useEffect(() => {
    if (!open) {
      setStep(0);
      setSelectedBaIds([]);
      setSupplierId(null);
      setCreateMode('new');
      setTargetPoId(undefined);
      setLineRows([]);
      setPaymentTerms('');
      setDeliveryTerms('');
      setQuotationReference('');
      setDepartmentId(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedBaIds.length) {
      setDepartmentId(undefined);
      return;
    }
    const fromBa = selectedBaIds
      .map((id) => baById.get(id))
      .map((ba) => ba?.department_id || ba?.supplierInquiry?.department_id)
      .find((id) => id && String(id).trim());
    if (fromBa) setDepartmentId(String(fromBa).trim());
  }, [selectedBaIds, baById]);

  useEffect(() => {
    setTargetPoId(undefined);
  }, [supplierId, createMode]);

  const loadSelectionsForBas = async (baIds: string[]): Promise<LineRow[]> => {
    setSelectionsLoading(true);
    try {
      const allRows: LineRow[] = [];
      for (const baId of baIds) {
        const ba = baById.get(baId);
        const selections: BidAnalysisSelection[] = await bidAnalysisService.getSelections(baId);
        for (const sel of selections) {
          if (!sel.selected) continue;
          const sq = sel.supplierQuotation;
          const supplierIdVal = sq?.supplier_id ?? '';
          if (!supplierIdVal) continue;
          const quoted = Number(sel.supplierQuotationLine?.quoted_quantity ?? 0);
          const converted = Number(sel.converted_quantity ?? 0);
          const remaining = Math.max(0, quoted - converted);
          if (remaining <= 0) continue;
          const lineProjectLabel = projectLabel({ supplierInquiryLine: sel.supplierInquiryLine });
          allRows.push({
            key: sel.id,
            selectionId: sel.id,
            bidAnalysisId: baId,
            analysisNumber: ba?.analysis_number ?? baId.slice(0, 8),
            itemDescription:
              sel.supplierInquiryLine?.item_description ??
              sel.supplierQuotationLine?.id ??
              'Line',
            itemCode: sel.supplierInquiryLine?.item_code,
            projectLabel: lineProjectLabel,
            hasProject: lineProjectLabel !== '—',
            supplierId: supplierIdVal,
            supplierName: sq?.supplier?.supplier_name ?? supplierIdVal,
            quotedQty: quoted,
            convertedQty: converted,
            remainingQty: remaining,
            unitPrice: Number(sel.negotiated_unit_price ?? sel.supplierQuotationLine?.unit_price ?? 0),
            awardQty: remaining,
            included: true,
          });
        }
      }
      setLineRows(allRows);
      const suppliers = [...new Map(allRows.map((r) => [r.supplierId, r.supplierName])).entries()];
      if (suppliers.length === 1) {
        setSupplierId(suppliers[0]![0]);
      } else {
        setSupplierId(null);
      }
      return allRows;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to load bid analysis lines');
      return [];
    } finally {
      setSelectionsLoading(false);
    }
  };

  const supplierOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of lineRows) {
      map.set(row.supplierId, row.supplierName);
    }
    return [...map.entries()].map(([id, name]) => ({ value: id, label: name }));
  }, [lineRows]);

  const filteredLines = useMemo(
    () => (supplierId ? lineRows.filter((r) => r.supplierId === supplierId) : []),
    [lineRows, supplierId]
  );

  const includedLines = filteredLines.filter((r) => r.included && r.awardQty > 0);

  const includedProjectLabels = useMemo(() => {
    const names = new Set<string>();
    for (const row of includedLines) {
      if (row.hasProject) names.add(row.projectLabel);
    }
    return names;
  }, [includedLines]);

  const linesMissingProject = useMemo(
    () => includedLines.filter((r) => !r.hasProject),
    [includedLines]
  );

  const selectedEligiblePo = useMemo(
    () => eligiblePos.find((p) => p.id === targetPoId),
    [eligiblePos, targetPoId]
  );

  const handleNext = async () => {
    if (step === 0) {
      if (!selectedBaIds.length) {
        message.warning('Select at least one bid analysis');
        return;
      }
      const rows = await loadSelectionsForBas(selectedBaIds);
      if (!rows.length) {
        message.warning('No remaining selected lines in the chosen bid analyses');
        return;
      }
      setStep(1);
      return;
    }
    if (step === 1) {
      if (!supplierId) {
        message.warning('Select a supplier');
        return;
      }
      if (!filteredLines.length) {
        message.warning('No convertible lines for this supplier in the selected bid analyses');
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (createMode === 'revise_existing' && !targetPoId) {
        message.warning('Select an existing Purchase Order to revise');
        return;
      }
      setStep(3);
      return;
    }
    if (step === 3) {
      if (!includedLines.length) {
        message.warning('Include at least one line with award quantity');
        return;
      }
      for (const row of includedLines) {
        if (row.awardQty > row.remainingQty + 0.0001) {
          message.warning(`Award qty exceeds remaining for ${row.itemDescription}`);
          return;
        }
      }
      if (includedLines.some((r) => !r.hasProject)) {
        message.error(
          'Cannot continue: included lines must have a project. Set project on the PR/inquiry line first.'
        );
        return;
      }
      setStep(4);
    }
  };

  useEffect(() => {
    if (step === 1 && selectedBaIds.length && lineRows.length === 0 && !selectionsLoading) {
      void loadSelectionsForBas(selectedBaIds);
    }
  }, [step]);

  const handleSubmit = async () => {
    if (!supplierId || !includedLines.length) return;
    if (linesMissingProject.length) {
      message.error(
        `Cannot create Purchase Order: ${linesMissingProject.length} line(s) missing project. Set project on the PR/inquiry line first.`
      );
      return;
    }
    if (createMode === 'revise_existing' && !targetPoId) {
      message.warning('Select an existing Purchase Order to revise');
      return;
    }
    if (createMode === 'new' && !departmentId?.trim()) {
      message.warning('Department is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload: CreateFromBidAnalysesPayload = {
        supplier_id: supplierId,
        bid_analysis_ids: [...new Set(includedLines.map((l) => l.bidAnalysisId))],
        lines: includedLines.map((l) => ({
          bid_analysis_selection_id: l.selectionId,
          award_qty: l.awardQty,
        })),
        payment_terms: paymentTerms.trim() || undefined,
        delivery_terms: deliveryTerms.trim() || undefined,
        quotation_reference: quotationReference.trim() || undefined,
        mode: createMode,
        target_po_id: createMode === 'revise_existing' ? targetPoId : undefined,
        department_id: createMode === 'new' ? departmentId?.trim() : undefined,
      };

      const result = await purchaseOrderService.createFromBidAnalyses(payload);
      message.success(
        createMode === 'revise_existing'
          ? 'Purchase Order revised from bid analyses'
          : 'Purchase Order created from bid analyses'
      );
      onClose();
      onSuccess?.();
      navigate(`/scm/procurement/purchase-orders/${result.purchaseOrder.id}`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to create Purchase Order');
    } finally {
      setSubmitting(false);
    }
  };

  const lineColumns: ColumnsType<LineRow> = [
    {
      title: 'Include',
      width: 70,
      render: (_, record) => (
        <input
          type="checkbox"
          checked={record.included}
          onChange={(e) =>
            setLineRows((prev) =>
              prev.map((r) => (r.key === record.key ? { ...r, included: e.target.checked } : r))
            )
          }
        />
      ),
    },
    { title: 'BA #', dataIndex: 'analysisNumber', width: 110 },
    {
      title: 'Project',
      dataIndex: 'projectLabel',
      width: 120,
      ellipsis: true,
      render: (v: string, r) =>
        r.hasProject ? v : <Text type="warning">{v}</Text>,
    },
    {
      title: 'Item',
      key: 'item',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{r.itemDescription}</Text>
          {r.itemCode && <Text type="secondary" style={{ fontSize: 11 }}>{r.itemCode}</Text>}
        </Space>
      ),
    },
    { title: 'Quoted', dataIndex: 'quotedQty', width: 80, align: 'right' },
    { title: 'Converted', dataIndex: 'convertedQty', width: 90, align: 'right' },
    { title: 'Remaining', dataIndex: 'remainingQty', width: 90, align: 'right' },
    {
      title: 'Award qty',
      width: 110,
      render: (_, record) => (
        <InputNumber
          min={0}
          max={record.remainingQty}
          value={record.awardQty}
          disabled={!record.included}
          style={{ width: '100%' }}
          onChange={(v) =>
            setLineRows((prev) =>
              prev.map((r) =>
                r.key === record.key ? { ...r, awardQty: Number(v ?? 0) } : r
              )
            )
          }
        />
      ),
    },
    {
      title: 'Unit price',
      dataIndex: 'unitPrice',
      width: 90,
      align: 'right',
      render: (v) => Number(v).toFixed(2),
    },
  ];

  const poOptions = (eligiblePos as EligiblePoForBaRevision[]).map((p) => ({
    value: p.id,
    label: `${p.po_number} · Rev ${p.revision_number} · ${p.po_status}${
      p.is_open_amendment_draft ? ' (open amendment)' : ''
    }`,
  }));

  return (
    <Modal
      title="Create PO from Bid Analysis"
      open={open}
      onCancel={onClose}
      width={920}
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          {step > 0 && (
            <Button onClick={() => setStep((s) => s - 1)} disabled={submitting}>
              Back
            </Button>
          )}
          {step < 4 ? (
            <Button type="primary" onClick={() => void handleNext()} loading={selectionsLoading}>
              Next
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={() => void handleSubmit()}
              loading={submitting}
              disabled={linesMissingProject.length > 0}
            >
              {createMode === 'revise_existing' ? 'Revise Purchase Order' : 'Create Purchase Order'}
            </Button>
          )}
        </Space>
      }
      destroyOnClose
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 20 }}
        items={[
          { title: 'Bid analyses' },
          { title: 'Supplier' },
          { title: 'PO mode' },
          { title: 'Lines' },
          { title: 'Confirm' },
        ]}
      />

      {step === 0 && (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Select approved bid analyses that still have lines to convert. Fully converted analyses are not shown."
          />
          <Table
            size="small"
            rowKey="id"
            loading={analysesLoading}
            dataSource={approvedAnalyses}
            pagination={false}
            scroll={{ y: 280 }}
            rowSelection={{
              selectedRowKeys: selectedBaIds,
              onChange: (keys) => setSelectedBaIds(keys as string[]),
            }}
            columns={[
              { title: 'Analysis #', dataIndex: 'analysis_number', width: 120 },
              {
                title: 'Supplier',
                key: 'supplier',
                ellipsis: true,
                render: (_: unknown, r: BidAnalysis) => r.supplier_name ?? '—',
              },
              {
                title: 'Inquiry',
                key: 'inquiry',
                width: 120,
                render: (_: unknown, r: BidAnalysis) =>
                  r.supplierInquiry?.inquiry_number ?? r.supplier_inquiry_id ?? '—',
              },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 90,
                render: (s: string) => <Tag color="green">{s}</Tag>,
              },
            ]}
          />
          {!analysesLoading && approvedAnalyses.length === 0 && (
            <Text type="secondary">No approved bid analyses with remaining lines to convert.</Text>
          )}
        </>
      )}

      {step === 1 && (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Choose the supplier for this purchase order. Only suppliers with remaining selected lines appear.
          </Text>
          <Select
            style={{ width: '100%' }}
            placeholder="Select supplier"
            value={supplierId ?? undefined}
            options={supplierOptions}
            onChange={setSupplierId}
            loading={selectionsLoading}
          />
          {supplierId && filteredLines.length === 0 && (
            <Alert type="warning" showIcon style={{ marginTop: 12 }} message="No remaining lines for this supplier." />
          )}
        </>
      )}

      {step === 2 && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>How should these awarded lines be ordered?</Text>
          <Radio.Group
            value={createMode}
            onChange={(e) => setCreateMode(e.target.value)}
            options={[
              { value: 'new', label: 'New Purchase Order' },
              { value: 'revise_existing', label: 'Revision of existing Purchase Order' },
            ]}
          />
          {createMode === 'revise_existing' && (
            <>
              <Text type="secondary">
                Same supplier ({currency}). Draft/Submitted POs get lines appended; Approved/Sent POs create an amendment Draft first.
              </Text>
              <Select
                showSearch
                allowClear
                style={{ width: '100%' }}
                placeholder="Select purchase order"
                loading={eligibleLoading}
                optionFilterProp="label"
                value={targetPoId}
                options={poOptions}
                onChange={(v) => setTargetPoId(v)}
              />
              {!eligibleLoading && eligiblePos.length === 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message="No eligible purchase orders for this supplier and currency."
                />
              )}
              {selectedEligiblePo && (
                <Alert
                  type="info"
                  showIcon
                  message={`${selectedEligiblePo.po_number} · ${selectedEligiblePo.po_status} · Rev ${selectedEligiblePo.revision_number}`}
                />
              )}
            </>
          )}
        </Space>
      )}

      {step === 3 && (
        <>
          {linesMissingProject.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="Some lines have no project. Set project on the PR/inquiry line before creating a Purchase Order."
            />
          )}
          <Table
            size="small"
            rowKey="key"
            columns={lineColumns}
            dataSource={filteredLines}
            pagination={false}
            scroll={{ y: 320 }}
          />
        </>
      )}

      {step === 4 && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>
            {createMode === 'revise_existing' ? 'Revising' : 'Creating'} PO for{' '}
            <strong>{supplierOptions.find((o) => o.value === supplierId)?.label}</strong>
            {createMode === 'revise_existing' && selectedEligiblePo ? (
              <>
                {' '}
                targeting <strong>{selectedEligiblePo.po_number}</strong>
              </>
            ) : null}{' '}
            with <strong>{includedLines.length}</strong> line(s) from{' '}
            <strong>{new Set(includedLines.map((l) => l.bidAnalysisId)).size}</strong> bid analysis(es).
          </Text>
          <Text>
            Project:{' '}
            <strong>
              {includedProjectLabels.size === 0
                ? '—'
                : includedProjectLabels.size === 1
                  ? [...includedProjectLabels][0]
                  : 'Multiple projects'}
            </strong>
          </Text>
          {linesMissingProject.length > 0 && (
            <Alert
              type="error"
              showIcon
              message="Some lines have no project. Set project on the PR/inquiry line before creating a Purchase Order."
            />
          )}
          {createMode === 'new' && (
            <>
              <div>
                <Text>
                  Department <Text type="danger">*</Text>
                </Text>
                <div style={{ marginTop: 4 }}>
                  <DepartmentSelect
                    value={departmentId}
                    onChange={(value) => setDepartmentId(value || undefined)}
                    style={{ width: '100%' }}
                    placeholder="Select department"
                  />
                </div>
              </div>
              <div>
                <Text type="secondary">Payment terms (optional)</Text>
                <Input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} />
              </div>
              <div>
                <Text type="secondary">Delivery terms (optional)</Text>
                <Input value={deliveryTerms} onChange={(e) => setDeliveryTerms(e.target.value)} />
              </div>
              <div>
                <Text type="secondary">Quotation reference (optional)</Text>
                <Input value={quotationReference} onChange={(e) => setQuotationReference(e.target.value)} />
              </div>
            </>
          )}
          {createMode === 'revise_existing' && (
            <Alert
              type="info"
              showIcon
              message="Existing PO header terms are kept. New awarded lines are appended (or added on a new amendment Draft)."
            />
          )}
        </Space>
      )}
    </Modal>
  );
};

export default CreatePOFromBidAnalysesModal;
